import { Injectable } from '@angular/core';
import { AngularFireDatabase } from 'angularfire2/database';
import { ICreditCard, IOrder, ICreditCardPayU } from '../models/payment.model';
import { Observable } from 'rxjs/Observable';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IUserShoppingCart } from '../models/user.model';
import { ICatalog } from '../models/catalog.model';
import { UtilitiesProvider } from './utilities';
import { Translate2Pipe } from '../pipes/translate2';
import { AuthData } from './auth';
import { ENV } from '@app/env';
import { TrackingData, _TRACKING_ENTITIES, _TRACKING_ACTIONS } from './tracking';
import { retry, catchError, tap } from 'rxjs/operators';
import { findIndex, isObject } from 'lodash';
import { IReserveShoppingCart, IAdditionalServiceShoppingCartItem, IReserveShoppingCartItem } from '../models/shopping-cart.model';

@Injectable()
export class PaymentProvider {

  private clientId = ENV.client;

  constructor(
    private afDatabase: AngularFireDatabase,
    private http: HttpClient,
    private utilities: UtilitiesProvider,
    private translateCode: Translate2Pipe,
    private tracking: TrackingData,
    private authData: AuthData,
  ) { }

  getClientHeader() {
    return this.http.get(`${ENV.pay_api_url}/client_token/${ENV.client}`)
      .map((res: any) => res.data.slice(2, res.data.length - 1));
  }

  getUserCards(userId: string): Observable<any> {
    switch (ENV.payment_provider) {
      case "paymentez":
        return this.getUserCardsPaymentez(userId);
      case "payu_gateway":
        return this.getUserCardsPayuGateway(userId);
    }
  }

  /**
   * Retorna tarjetas de credito valid de la pasarela de pago paymentez
   * @param userId
   * @author Juan Pablo C Mejia <jcardona@ideabti.com>
   * 27-08-2018
   */
  getUserCardsPaymentez(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.get(`${ENV.pay_api_url}/v1/paymentez/cards/${userId}/${this.clientId}`, { headers });
    });
  }

  /**
   * Retorna tarjetas de credito valid de la pasarela de pago payu_gateway
   * @param userId
   * @author Juan Pablo C Mejia <jcardona@ideabti.com>
   * 16-01-2019
   */
  getUserCardsPayuGateway(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.get(`${ENV.pay_api_url}/v2/pay_u_gateway/cards/${userId}/${this.clientId}`, { headers });
    });
  }

  /**
   * @Param user
   *
   *  Retorna tarjetas de credito de PayU
   * **/
  getUserCardsPayu(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.get(`${ENV.pay_api_url}/v1/pay_u/cards/${userId}`, { headers });
    });
  }

  addCreditCard(card: ICreditCard, user: { id: string, email: string, dni: string }): Promise<any> {
    switch (ENV.payment_provider) {
      case "paymentez":
        return this.addCreditCardPaymentez(card, user);
      case "payu_gateway":
        return this.addCreditCardPayU(card, user);
    }
  }

  /**
   * Envia peticion para adicionar una tarjeta de credito,
   * Ver endpoint en https://github.com/ideaBTi/RebusV3.6-payment/tree/develop#add-card
   * @param card
   * @param user
   * @author Juan Pablo C Mejia <jcardona@ideabti.com>
   * 27-08-2018
   */
  addCreditCardPaymentez(card: ICreditCard, user: { id: string, email: string }): Promise<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v1/paymentez/card/add/${this.clientId}`, { card, user }, { headers });
    }).toPromise();
  }

  addCreditCardPayU(card: ICreditCard, user: { id: string, email: string }): Promise<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u_gateway/card/add/${this.clientId}`, { card, user }, { headers });
    }).toPromise();
  }

  public getUserShoppingCart(userId: string) {
    return this.afDatabase.object<IUserShoppingCart>(`/Clients/${this.clientId}/UserShoppingCart/${userId}`)
      .valueChanges();
  }

  public updateUserShoppingCart(userId: string, shopping: any): Promise<any> {
    return this.afDatabase.object<IUserShoppingCart>(`/Clients/${this.clientId}/UserShoppingCart/${userId}`)
      .update(shopping)
  }

  public getUserShoppingCartSnap(userId: string) {
    return this.afDatabase.object<IUserShoppingCart>(`/Clients/${this.clientId}/UserShoppingCart/${userId}`).query;
  }

  public getTicketsPayment(transactionId, userId) {
    return this.afDatabase.object<any>(`/Clients/${this.clientId}/UserPaymentHistory/${userId}/${transactionId}/summary/tickets`).query.ref;
  }

  public setTicketPayment(ticketId) {
    return this.afDatabase.object(`/Clients/${this.clientId}/Ticket/${ticketId}/isPayFeed`).set(true);
  }

  public getReservesShoppingCart(uid: string) {
    return this.afDatabase.object<IReserveShoppingCart>(
      `/Clients/${this.clientId}/UserReserveShoppingCart/${uid}`
    ).valueChanges();
  }

  public getReservesShoppingCartSnap(uid: string) {
    return this.afDatabase.object<IReserveShoppingCart>(
      `/Clients/${this.clientId}/UserReserveShoppingCart/${uid}`
    ).query;
  }

  public getShopByIdSnap(shopId: string): Promise<any> {
    return this.afDatabase.object<any>(`/ClientList/${this.clientId}/shops/${shopId}`)
      .query.once("value").then((snap) => snap.val());
  }

  public addProductToShoppingCart(userId: string, product: any, amount: number) {
    return this.getUserShoppingCartSnap(userId).ref.once('value')
      .then(snap => snap.val())
      .then(cart => {
        if (cart) {
          let productShopping = findIndex(cart.products, ['id', product.id]);
          if (productShopping !== -1) {
            cart.products[productShopping].sale_price = product.price;
            cart.products[productShopping].sale_amount = amount;
          } else {
            product.sale_amount = amount;
            product.sale_price = product.price;
            cart.products[product.id] = product;
          }
        } else {
          product.sale_amount = amount;
          product.sale_price = product.price;
          cart = {
            state: 'pending',
            created_at: new Date().getTime(),
            products: { [product.id]: product },
          };
        }
        return cart;
      })
      .then(cart => {
        if (isObject(cart)) {
          // Esta actualizacion dispara la funcion que recalcula valores y descuentos del carrito de compra
          this.afDatabase.object<IUserShoppingCart>(`/Clients/${this.clientId}/UserShoppingCart/${userId}`)
            .update({ ...cart, isLoad: true });
        }
        return cart;
      })
      .then((cart) => cart)
  }

  public applyDiscount(shoppingCart: any, userId: string): Promise<any> {
    // Esta actualizacion dispara la funcion que recalcula valores y descuentos del carrito de compra
    return this.afDatabase.object(`/Clients/${this.clientId}/UserShoppingCart/${userId}`)
      .update({ ...shoppingCart, isLoad: true });
  }

  public applyDiscountTicket(shoppingCart: any, userId: string): Promise<any> {
    // Esta actualizacion dispara la funcion que recalcula valores y descuentos del carrito de compra
    return this.afDatabase.object(`/Clients/${this.clientId}/UserTicketShoppingCart/${userId}`)
      .update({ ...shoppingCart, isLoad: true });
  }

  public applyDiscountServices(shoppingCart: any, userId: string): Promise<any> {
    // Esta actualizacion dispara la funcion que recalcula valores y descuentos del carrito de compra
    return this.afDatabase.object(`/Clients/${this.clientId}/UserServicesShoppingCart/${userId}`)
      .update({ ...shoppingCart, isLoad: true });
  }

  removeProductFromShoppingCart(userId: string, index: string) {
    return this.afDatabase.object(`/Clients/${this.clientId}/UserShoppingCart/${userId}`)
      .query.ref.transaction((cart: any) => {
        let newObj = Object.keys(cart.products).map((k) => {
          if (cart.products[k].id !== index) {
            return cart.products[k];
          } else {
            return null;
          }
        });
        const newProducts = newObj.reduce((total, next) => {
          if (next) {
            return { ...total, [next.id]: next };
          } else {
            return { ...total };
          }
        }, {});

        if (Object.keys(newProducts).length === 0) {
          return null;
        } else {
          return { ...cart, products: newProducts };
        }
      });
  }


  removeTicketFromShoppingCart(userId: string, index: string) {
    return this.afDatabase.object(`/Clients/${this.clientId}/UserTicketShoppingCart/${userId}`)
      .query.ref.transaction((cart: any) => {
        let newObj = Object.keys(cart.tickets).map((k) => {
          if (cart.tickets[k].id !== index) {
            return cart.tickets[k];
          } else {
            return null;
          }
        });
        const newTickets = newObj.reduce((total, next) => {
          if (next) {
            return { ...total, [next.id]: next };
          } else {
            return { ...total };
          }
        }, {});

        if (Object.keys(newTickets).length === 0) {
          return null;
        } else {
          return { ...cart, tickets: newTickets };
        }
      });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart con payu
   * @param userId
   */
  public transactionShoppingCartPayu(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u/payment`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart con payu gateway
   * @param userId
   */
  public transactionShoppingCartPayuGateway(userId: string, cvv2: number, tokenCard: string, installments: number): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u_gateway/transaction`, { user: userId, client: ENV.client, token: tokenCard, cvv2, installments }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart con payu gateway
   * @param userId
   */
  public transactionReservePayuGateway(userId: string, cvv2: number, tokenCard: string, installments: number): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u_gateway/reserve`, { user: userId, client: ENV.client, token: tokenCard, cvv2, installments }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart tickets con payu
   * @param userId
   */
  public transactionShoppingCartTicketPayu(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u/tickets`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart tickets con payu
   * @param userId
   */
  public transactionShoppingCartServicesPayu(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u/services`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart tickets con payu
   * @param userId
   */
  public transactionTicketPayuGateway(userId: string, cvv2: number, tokenCard: string, installments: number): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u_gateway/tickets`, { user: userId, client: ENV.client, token: tokenCard, cvv2, installments }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart tickets con payu
   * @param userId
   */
  public transactionServicesPayuGateway(userId: string, cvv2: number, tokenCard: string, installments: number): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u_gateway/services`, { user: userId, client: ENV.client, token: tokenCard, cvv2, installments }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart con paymentez
   * @param userId
   */
  public transactionShoppingCartPaymentez(tokenCard: string, userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/paymentez/transaction`, { user: userId, client: ENV.client, token: tokenCard }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart con pagos Fake
   * @param userId
   */
  public transactionShoppingCartFake(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/fake/transaction`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart ticket con paymentez
   * @param userId
   */
  public transactionShoppingCartTicketPaymentez(tokenCard: string, userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/paymentez/tickets`, { user: userId, client: ENV.client, token: tokenCard }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart ticket con pagos fake
   * @param userId
   */
  public transactionShoppingCartTicketFake(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/fake/tickets`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart con zeus
   * @param userId
   */
  public transactionShoppingCartZeus(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/zeus/transaction`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart ticket con zeus
   * @param userId
   */
  public transactionShoppingCartTicketZeus(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/zeus/tickets`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart ticket con zeus
   * @param userId
   */
  public transactionShoppingCartServicesZeus(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/zeus/services`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart ticket con paymentez
   * @param userId
   */
  public transactionShoppingCartServicesPaymentez(tokenCard: string, userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/paymentez/services`, { user: userId, client: ENV.client, token: tokenCard }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para el shopping cart ticket con pagos fake
   * @param userId
   */
  public transactionShoppingCartServicesFake(userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/services/fake`, { user: userId, client: ENV.client }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para la reservas con paymentez
   * @param tokenCard
   * @param userId
   */
  public transactionReservePaymentez(tokenCard: string, userId: string, entity_id: string, entity_type: 'event' | 'staff' | 'stand', coupon: string = null, event_id: string = null, calendars: string[] = null): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/paymentez/reserve`, {
        user: userId,
        client: ENV.client,
        token: tokenCard,
        entity_type,
        entity_id,
        eventid: event_id,
        coupon,
        calendars
      }, { headers });
    });
  }

  public transactionReserveShoppingCartPaymentez(tokenCard: string, uid: string) {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/paymentez/reserve`, { user: uid, client: ENV.client, token: tokenCard }, { headers });
    });
  }

  public transactionReserveShoppingCartPayU(uid: string) {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u/reserve`, { user: uid, client: ENV.client, token: null }, { headers });
    });
  }

  public transactionReserveShoppingCartFake(uid: string) {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/fake/reserve`, { user: uid, client: ENV.client, token: null }, { headers });
    });
  }

  public transactionReserveShoppingCartZeus(uid: string) {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/zeus/reserve`, { user: uid, client: ENV.client, token: null }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para la reservas con servicio Fake
   * @param tokenCard
   * @param userId
   */
  public transactionReserveFake(userId: string, entity_id: string, entity_type: 'event' | 'staff' | 'stand', coupon: string = null, event_id: string = null, calendars: string[] = null): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/fake/reserve`, {
        user: userId,
        client: ENV.client,
        token: null,
        entity_type,
        entity_id,
        eventid: event_id,
        coupon,
        calendars
      }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para la reservas con payu
   * @param tokenCard
   * @param userId
   */
  public transactionReservePayu(tokenCard: string, userId: string, entity_id: string, entity_type: 'event' | 'staff' | 'stand', coupon: string = null, event_id: string = null, calendars: string[] = null): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u/reserve`, {
        user: userId,
        client: ENV.client,
        token: tokenCard,
        entity_type,
        entity_id,
        eventid: event_id,
        coupon,
        calendars
      }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de pago para la reservas con zeus
   * @param tokenCard
   * @param userId
   */
  public transactionReserveZeus(userId: string, entity_id: string, entity_type: 'event' | 'staff' | 'stand', coupon: string = null, event_id: string = null, calendars: string[] = null): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/zeus/reserve`, {
        user: userId,
        client: ENV.client,
        entity_type,
        entity_id,
        eventid: event_id,
        coupon,
        calendars
      }, { headers });
    });
  }

  /**
   * Este metodo invoca la peticion de cancelar una reserva con payu /paymentez o sin pago
   * @param reserveId
   * @param userId
   */
  public transactionCancelReserve(paymentId: string, userId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/reserve/cancel`, {
        client: ENV.client,
        user: userId,
        payment: paymentId
      }, { headers });
    });
  }

  /**
   *
   * Envia peticion para transaccion con paymentez,
   * Ver endpoint en https://github.com/ideaBTi/RebusV3.6-payment/tree/develop#make-payment
   * @param userId
   * @param userEmail
   * @param shoppingCart
   * @param cardToken
   * @param payload
   * @author Juan Pablo C Mejia <jcardona@ideabti.com>
   * 27-08-2018
   */
  doTransactionPaymentez(userId: string, userEmail: string, shoppingCart: IUserShoppingCart, cardToken: string, payload: IOrder) {
    const key = this.afDatabase.createPushId();
    const dev_reference = JSON.stringify({ client: this.clientId, user: userId, key });
    const transactionData = {
      user: { id: userId, email: userEmail },
      order: { ...payload, dev_reference, description: `Catalog transaction with cart id: ${key}` },
      card: { token: cardToken }
    };
    this.utilities.consoleDecorator('Request: ', transactionData);

    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v1/paymentez/transaction/${this.clientId}`, transactionData, { headers })
      // .map((response: any) => {
      //   this.registerTransactionLog('Catalog transaction with cart', response, userId);
      //   return { key, response }
      // })
      // .catch(e => {
      //   return this.registerTransactionLog('Catalog transaction with cart error', e.error, userId);
      // });
    });
  }

  doReserveTransactionPaymentez(userId: string, userEmail: string, reserve: any, cardToken: string, payload: IOrder) {
    const key = this.afDatabase.createPushId();
    const dev_reference = JSON.stringify({ client: this.clientId, user: userId, key });
    const transactionData = {
      user: { id: userId, email: userEmail },
      order: { ...payload, dev_reference, description: `Reserve transaction with reserve id: ${reserve.id}` },
      card: { token: cardToken }
    };
    this.utilities.consoleDecorator('Request: ', transactionData);

    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v1/paymentez/transaction/${this.clientId}`, transactionData, { headers })
        .map((response: any) => {
          this.registerTransactionLog('Catalog transaction with cart', response, userId);
          return { key, response }
        })
        .catch(e => {
          return this.registerTransactionLog('Catalog transaction with cart error', e.error, userId);
        });
    })
  }

  public removeUserShoppingCart(userId: string, cart: any, transaction: any) {
    // Se llama el servicio de AI donde se incluye la compra del usuario
    // TODO: Se debe verificar si se envia siempre a AI o solo cuando el resultado de la transaccion sea success o pending
    const randomId = this.afDatabase.createPushId();
    const payload = {
      ...cart,
      status: transaction.status,
      id: randomId,
      clientId: this.clientId,
      userId: userId
    }
    this.tracking.trackEntity('shopping_carts', randomId, this.clientId, payload, 'create');

    return this.afDatabase.object(`/Clients/${this.clientId}/UserShoppingCart/${userId}`)
      .set(null);
  }

  saveTransaction(userId: string, key: string, data: any, type = 'catalog') {
    const transaction = { ...data, type, userId };
    return this.afDatabase.object(`/Clients/${this.clientId}/UserPaymentHistory/${userId}/${key}`)
      .set(transaction);
  }

  saveNoPayTransaction(userId: string, data: any, type: string) {
    const id = this.afDatabase.createPushId();
    if (!data.transaction.dev_reference) {
      const dr = { client: ENV.client, user: userId, key: id };
      data.transaction.dev_reference = dr;
    }
    const transaction = { ...data, id, type, userId };
    return this.afDatabase.object(`/Clients/${this.clientId}/UserPaymentHistory/${userId}/${id}`)
      .set(transaction).then(() => id);
  }

  getPaymentHistory(userId: string) {
    return this.afDatabase.list<any>(`/Clients/${this.clientId}/UserPaymentHistory/${userId}`).valueChanges();
  }

  getFormShoppingTicket(userId?, paymentId?, ticketId?) {
    return this.afDatabase.object(`/Clients/${this.clientId}/UserPaymentHistory/${userId}/${paymentId}/summary/tickets/${ticketId}`).query.ref;
  }

  getPaymentHistoryById(userId: string, transactionId: string) {
    return this.afDatabase.object(`/Clients/${this.clientId}/UserPaymentHistory/${userId}/${transactionId}`).valueChanges();
  }

  removeCreditCard(userId: string, card: { id: string, token: string }): Promise<any> {
    switch (ENV.payment_provider) {
      case "paymentez":
        return this.removeCreditCardPaymentez(userId, card);
      case "payu_gateway":
        return this.removeCreditCardPayu(userId, card);
    }
  }

  /**
   * Envia peticion para eliminar una tarjeta de credito,
   * Ver endpoint en https://github.com/ideaBTi/RebusV3.6-payment/tree/develop#delete-card
   * @param userId
   * @param card
   * @author Juan Pablo C Mejia <jcardona@ideabti.com>
   * 23-08-2018
   */
  removeCreditCardPayu(userId: string, card: { id: string, token: string }): Promise<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v2/pay_u_gateway/deleteCard/${this.clientId}`, {
        card: { token: card.token },
        user: { id: userId }
      }, { headers });
    }).toPromise();
  }

  /**
   * Envia peticion para eliminar una tarjeta de credito paymentez,
   * Ver endpoint en https://github.com/ideaBTi/RebusV3.6-payment/tree/develop#delete-card
   * @param userId
   * @param card
   * @author Juan Pablo C Mejia <jcardona@ideabti.com>
   * 23-08-2018
   */
  removeCreditCardPaymentez(userId: string, card: { id: string, token: string }): Promise<any> {
    return Observable.fromPromise(this.authData.getUser().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ 'Authorization': token });
      return this.http.post(`${ENV.pay_api_url}/v1/paymentez/deleteCard/${this.clientId}`, {
        card: { token: card.token },
        user: { id: userId }
      }, { headers });
    }).toPromise();
  }

  reduceCatalogStock(catalogId: string, quantity: number) {
    return this.afDatabase.object(`/Clients/${this.clientId}/Catalogs/${catalogId}/stock`)
      .query.ref.transaction(val => {
        if (val) return val - quantity;
        return val;
      });
  }

  registerTransactionLog(type: string, response: any, userId: string) {
    // Se debe limitar el envio de textos muy largos en el error
    if (response.error) {
      if (response.error.error) {
        if (response.error.error.length > 1500) {
          response.error.error = `${response.error.error.substr(0, 1500)}... [ REBUS-FAIL::ExceededCharacterLimit->${response.error.error.length} ]`;
        }
      }
    }
    const payload = {
      client_id: this.clientId,
      response,
      type,
      action: 'REBUS_LOG',
      platform: this.utilities.getInfoUserDevice(),
      userId,
      timestamp: new Date().getTime()
    }

    return this.http.post(`${ENV.backend}/logs/create`, payload)
      .pipe(
        retry(2),
        tap(() => this.utilities.consoleDecorator(`Tracking transaction: ${type}`, payload, 'orange')),
        catchError(err => [this.utilities.consoleDecorator('Error tracking stats: ', err, 'orange')])
      ).toPromise();
  }

  async checkProductAvailability(catalogId: string, requiredQuantity: number) {
    const ref = this.afDatabase.object<ICatalog>(`/Clients/${this.clientId}/Catalogs/${catalogId}`);
    const catalog = await ref.query.once('value').then(snap => snap.val());
    this.utilities.consoleDecorator('Catalog: ', catalog)
    this.utilities.consoleDecorator('Catalog id: ', catalogId)
    this.utilities.consoleDecorator('Required: ', requiredQuantity)
    if (!catalog) return { id: catalogId, status: false };
    const catalogName = await this.translateCode.transform(catalog.name);
    const stock = catalog.stock;
    if (stock < requiredQuantity) return { id: catalogId, name: catalogName, status: false };
    return { id: catalogId, name: catalogName, status: true };
  }

  setDefaultCard(uid: string, token: string) {
    return this.afDatabase.object(`/Clients/${this.clientId}/Users/${uid}/defaultCard`).set(token);
  }


  public addFormToProduct(userId: string, sale_form_user_data: any): Promise<any> {
    return this.afDatabase.object(`/Clients/${ENV.client}/UserShoppingCart/${userId}/sale_form_user_data`)
      .update({ ...sale_form_user_data });
  }

  public addFormFieldsToProduct(userId: string, forms: any[]): Promise<any> {
    return this.afDatabase.object<IUserShoppingCart>(`/Clients/${this.clientId}/UserShoppingCart/${userId}`).query.once("value")
      .then(snap => snap.val())
      .then(cart => {
        console.log(cart)
        console.log(forms)
        Object.keys(cart.products).map(k => {
          const form: any[] = forms.filter(item => item.id === cart.products[k].shop);
          if (form.length > 0) {
            cart.products[k].formShopping = form[0].formShopping;
          }
        })
        return cart
      })
      .then(cart => this.updateUserShoppingCart(userId, cart));
  }

  public setReserveShoppingCartSaleCalendars(calendars: string[], reserve: string, uid: string) {
    return this.afDatabase.object(
      `/Clients/${ENV.client}/UserReserveShoppingCart/${uid}/reserves/${reserve}/sale_calendar`
    ).set(calendars).then(() => this.dispatchReserveShoppingCartCalc(uid));
  }

  public setReserveShoppingCartServices(services: IAdditionalServiceShoppingCartItem[], reserve: string, uid: string) {
    return this.afDatabase.object(
      `/Clients/${ENV.client}/UserReserveShoppingCart/${uid}/reserves/${reserve}/services`
    ).set(services).then(() => this.dispatchReserveShoppingCartCalc(uid));
  }

  public dispatchReserveShoppingCartCalc(uid: string) {
    return this.afDatabase.object(
      `/Clients/${ENV.client}/UserReserveShoppingCart/${uid}/isLoad`
    ).set(true);
  }

  public updateAndRefreshReservesShoppingCart(cart: IReserveShoppingCart, uid: string) {
    return this.afDatabase.object(
      `/Clients/${ENV.client}/UserReserveShoppingCart/${uid}`
    ).update({ ...cart, isLoad: true });
  }

  public deleteReserveFromShoppingCart(uid: string, reserve: string) {
    return this.afDatabase.object(
      `/Clients/${ENV.client}/UserReserveShoppingCart/${uid}/reserves/${reserve}`
    ).set(null).then(() => this.dispatchReserveShoppingCartCalc(uid));
  }

}



// WEBPACK FOOTER //
// ./src/providers/payment.ts